
import { defineComponent } from 'vue'

import ActiveUserMixin from '~/mixins/activeUserMixin'
import RepoListMixin from '~/mixins/repoListMixin'
import { Repository, RepositoryKindChoices } from '~/types/types'

export default defineComponent({
  name: 'SidebarRecentlyActive',
  mixins: [ActiveUserMixin, RepoListMixin],
  props: {
    isCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dropdownCollapsed: false
    }
  },
  async fetch() {
    await this.fetchActiveUser()
    await this.fetchRepos()
  },
  mounted() {
    this.$socket.$on('repo-onboarding-completed', () => {
      this.fetchRepos(true)
    })
    const { provider, owner } = this.$route.params

    this.dropdownCollapsed =
      (this.$localStore.get('ui-state', `is-sidebar-collapsed-${provider}-${owner}`) as boolean) ??
      true
  },
  beforeDestroy() {
    this.$socket.$off('repo-onboarding-completed')
  },
  methods: {
    async fetchRepos(refetch = false): Promise<void> {
      this.fetchActiveAnalysisRepoList({
        login: this.activeOwner,
        provider: this.activeProvider,
        refetch
      })
    },
    getIcon({ isPrivate, kind }: Pick<Repository, 'isPrivate' | 'kind'>): string {
      if (kind === RepositoryKindChoices.Subrepo) {
        return isPrivate ? 'folder-locked' : 'folder-globe'
      }

      return isPrivate ? 'z-lock' : 'globe'
    },
    toggleDropdown() {
      if (this.repoList.length > 0) {
        this.dropdownCollapsed = !this.dropdownCollapsed
      }
    },
    buildRoute(repo: Record<string, unknown>): string {
      return `/${this.activeProvider}/${repo.owner ?? this.activeOwner}/${repo.name}`
    },
    updateLocalState() {
      const { provider, owner } = this.$route.params

      if (process.client)
        this.$localStore.set(
          'ui-state',
          `is-sidebar-collapsed-${provider}-${owner}`,
          this.dropdownCollapsed
        )
    }
  },
  watch: {
    'activeDashboardContext.id': {
      handler() {
        this.fetchRepos(true).catch(() => {
          // Intentionally ignoring errors from fetchRepos
        })
      }
    },
    dropdownCollapsed: {
      handler() {
        this.updateLocalState()
      }
    }
  },
  computed: {
    repoList(): Array<Record<string, unknown>> {
      if (this.repoWithActiveAnalysis) {
        return this.repoWithActiveAnalysis.map(
          ({ id, displayName, isPrivate, kind, name, ownerLogin }) => {
            const icon = this.getIcon({ isPrivate, kind })

            return {
              id,
              displayName,
              name,
              owner: ownerLogin,
              icon,
              isActive: false
            }
          }
        )
      }
      return []
    },
    isActive(): boolean {
      if (!this.$route.name) return false

      return (
        this.$route.name === 'provider-owner-repo' ||
        this.$route.name?.startsWith('provider-owner-repo-')
      )
    },
    route(): string {
      return `/${this.activeProvider}/${this.activeOwner}`
    }
  }
})
